import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Checkbox, Col, ControlLabel, FormControl, FormGroup, HelpBlock, InputGroup } from 'react-bootstrap';
import { ModalDialog, Form } from 'axway-react-ui';
import * as utils from 'components/utility/utils';

class EditField extends ModalDialog.Content {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.onChangeName = this.onValueChanged.bind(this, 'fieldName');
		this.onChangeType = this.onValueChanged.bind(this, 'fieldType');
		this.onChangeDefaultValue = this.onValueChanged.bind(this, 'defaultValue');
		this.onChangeDescription = this.onValueChanged.bind(this, 'fieldDescription');
		this.onChangeRequired = this.onCheckedChanged.bind(this, 'fieldRequired');
		this.onChangeReadOnly = this.onCheckedChanged.bind(this, 'fieldReadOnly');
		this.validateFieldName = this.validateFieldName.bind(this);
		this.validateDefaultValue = this.validateDefaultValue.bind(this);
		this.validatesTrue = this.validatesTrue.bind(this);

		this.registerFieldValidation('fieldName', this.validateFieldName);
		this.registerFieldValidation('fieldType');
		this.registerFieldValidation('fieldDescription');
		this.registerFieldValidation('defaultValue', this.validateDefaultValue);
		this.registerFieldValidation('fieldRequired');
		this.registerFieldValidation('fieldReadOnly');
	}

	onValueChanged(field, ev) {
		this.setFieldState(field, ev.target.value, () => {
			this.changed();
		});
	}

	onCheckedChanged(field, ev) {
		this.setFieldState(field, ev.target.checked, () => {
			this.changed();
		});
	}

	isRestrictedFieldName(name) {
		// APIBuilder currently hates it if name == id
		return name === 'id';
	}

	validateFieldName() {
		const value = utils.trim(this.state.fieldName);
		return value.length > 0
			&& !this.isDuplicateFieldName(value)
			&& !this.isRestrictedFieldName(value);
	}

	isDuplicateFieldName(name) {
		// filter out this field name from the list of existing field names.
		return this.props.existingFieldNames
			.filter(a => a !== this.props.fieldName)
			.indexOf(name) >= 0;
	}

	validateDefaultValue() {
		const value = this.state.defaultValue;
		if (!value) {
			return true;
		}
		if (this.state.fieldType === 'number') {
			return utils.isNumber(value);
		} else if (this.state.fieldType === 'boolean') {
			return utils.isBool(value);
		} else if (this.state.fieldType === 'object') {
			return utils.isObject(value);
		} else if (this.state.fieldType === 'array') {
			return utils.isArray(value);
		}
		return true;
	}

	validatesTrue() {
		return true;
	}

	toField() {
		return {
			fieldType: utils.stringTypeToObject(this.state.fieldType),
			fieldDescription: utils.trim(this.state.fieldDescription),
			defaultValue: utils.trim(this.state.defaultValue),
			fieldReadOnly: this.state.fieldReadOnly,
			fieldRequired: this.state.fieldRequired,
			fieldName: utils.trim(this.state.fieldName),
			originalFieldName: this.props.originalFieldName,
			source: this.props.source
		};
	}

	changed() {
		const { onChange } = this.props;
		if (!onChange) {
			return;
		}
		const field = this.toField();
		onChange(field);
	}

	render() {
		const { fieldName, fieldType, defaultValue,
			fieldDescription, fieldRequired, fieldReadOnly, validate } = this.state;
		const fieldNameProps = this.getFieldValidationProps('fieldName');
		const defaultValueProps = this.getFieldValidationProps('defaultValue');

		let errorFieldNameEmpty = false;
		let errorFieldNameDuplicate = false;
		let errorFieldNameRestricted = false;
		// if a field name error, determine the reason
		if (fieldNameProps.validationState === 'error') {
			if (this.isDuplicateFieldName(fieldName)) {
				errorFieldNameDuplicate = true;
			} else if (this.isRestrictedFieldName(fieldName)) {
				errorFieldNameRestricted = true;
			} else {
				errorFieldNameEmpty = true;
			}
		}
		const lcFieldType = utils.objectTypeToString(fieldType);

		return (
			<Form
				horizontal
				className="edit-fields"
				onSubmit={this.props.doValidate}
			>
				<FormGroup controlId="nameCtrl" className="field-name-group" {...fieldNameProps}>
					<Col componentClass={ControlLabel} sm={3}>
						{this.intl.formatMsg('Field name')}:
					</Col>
					<Col sm={9}>
						<InputGroup>
							<FormControl
								type="text"
								className="field-name"
								autoComplete="off"
								autoCorrect="off"
								spellCheck="off"
								placeholder={`(${this.intl.formatMsg('required')})`}
								onChange={this.onChangeName}
								value={fieldName}
							/>
							<InputGroup.Addon>*</InputGroup.Addon>
						</InputGroup>
						{(validate && (errorFieldNameEmpty)) && (
							<HelpBlock>
								{this.intl.formatMsg('Field name is required.')}
							</HelpBlock>
						)}
						{(validate && (errorFieldNameDuplicate)) && (
							<HelpBlock>
								{this.intl.formatMsg('A field already exists with this name.')}
							</HelpBlock>
						)}
						{(validate && (errorFieldNameRestricted)) && (
							<HelpBlock>
								{this.intl.formatMsg('Field cannot have the specified value.')}
							</HelpBlock>
						)}
					</Col>
				</FormGroup>

				<FormGroup controlId="typeCtrl" className="field-type-group">
					<Col componentClass={ControlLabel} sm={3}>
						{this.intl.formatMsg('Type')}:
					</Col>
					<Col sm={9}>
						<FormControl
							className="field-type"
							componentClass="select"
							placeholder="select"
							onChange={this.onChangeType}
							value={lcFieldType}
							disabled={this.props.disableType}
						>
							<option value="string">{this.intl.formatMsg('String')}</option>
							<option value="number">{this.intl.formatMsg('Number')}</option>
							<option value="boolean">{this.intl.formatMsg('Boolean')}</option>
							<option value="object">{this.intl.formatMsg('Object')}</option>
							<option value="array">{this.intl.formatMsg('Array')}</option>
							<option value="date">{this.intl.formatMsg('Date')}</option>
						</FormControl>
					</Col>
				</FormGroup>

				<FormGroup controlId="defaultCtrl" className="default-value-group" {...defaultValueProps}>
					<Col componentClass={ControlLabel} sm={3}>
						{this.intl.formatMsg('Default value')}:
					</Col>
					<Col sm={9}>
						<FormControl
							type="text"
							className="default-value"
							placeholder={this.intl.formatMsg('(optional)')}
							onChange={this.onChangeDefaultValue}
							value={defaultValue}
						/>
						{(validate && (defaultValueProps.validationState === 'error') && (this.state.fieldType === 'number')) && (
							<HelpBlock>
								{this.intl.formatMsg('Not a valid number. Expected digits.')}
							</HelpBlock>
						)}
						{(validate && (defaultValueProps.validationState === 'error') && (this.state.fieldType === 'boolean')) && (
							<HelpBlock>
								{this.intl.formatMsg('Not a valid boolean.  Expected true, or false.')}
							</HelpBlock>
						)}
						{(validate && (defaultValueProps.validationState === 'error') && (this.state.fieldType === 'object')) && (
							<HelpBlock>
								{this.intl.formatMsg('Not a valid object.  Expected valid JSON.')}
							</HelpBlock>
						)}
						{(validate && (defaultValueProps.validationState === 'error') && (this.state.fieldType === 'array')) && (
							<HelpBlock>
								{this.intl.formatMsg('Not a valid object.  Must be valid JSON Array.')}
							</HelpBlock>
						)}
					</Col>
				</FormGroup>

				<FormGroup controlId="descriptionCtrl" className="field-description-group">
					<Col componentClass={ControlLabel} sm={3}>
						{this.intl.formatMsg('Description')}:
					</Col>
					<Col sm={9}>
						<FormControl
							componentClass="textarea"
							className="field-description vresize"
							onChange={this.onChangeDescription}
							placeholder={this.intl.formatMsg('(optional)')}
							value={fieldDescription}
						/>
					</Col>
				</FormGroup>

				<FormGroup controlId="optionsCtrl">
					<Col sm={3} />
					<Col sm={3}>
						<Checkbox
							inline
							className="field-read-only"
							checked={fieldReadOnly}
							onChange={this.onChangeReadOnly}
						>
							{this.intl.formatMsg('Read-only')}
						</Checkbox>
					</Col>
					<Col sm={6}>
						<Checkbox
							inline
							className="field-required"
							checked={fieldRequired}
							onChange={this.onChangeRequired}
						>
							{this.intl.formatMsg('Required')}
						</Checkbox>
					</Col>
				</FormGroup>
			</Form>
		);
	}
}

EditField.propTypes = {
	/**
	 * A list of existing field names on which to validate this field name.
	 */
	existingFieldNames: PropTypes.arrayOf(PropTypes.string),

	/**
	 * The original field name.
	 */
	originalFieldName: PropTypes.string,

	/**
	 * Field name.
	 */
	fieldName: PropTypes.string,

	/**
	 * Field type.
	 */
	fieldType: PropTypes.string,

	/**
	 * Field description.
	 */
	fieldDescription: PropTypes.string,

	/**
	 * Field read-only.
	 */
	fieldReadOnly: PropTypes.bool,

	/**
	 * Field required.
	 */
	fieldRequired: PropTypes.bool,

	/**
	 * Field source.
	 */
	source: PropTypes.string,

	/**
	 * Part of the contract with ModalDialog.Content.  This dialog must
	 * render validation state.
	 */
	validate: PropTypes.bool,

	/**
	 * Callback for when the field changes.
	 */
	onChange: PropTypes.func,

	/**
	 * Disables editing the type.
	 */
	disableType: PropTypes.bool,

	/**
	 * Submit action to be called on Return key pressed
	 */
	onSubmit: PropTypes.func
};

EditField.defaultProps = {
	existingFieldNames: [],
	originalFieldName: '',
	fieldName: '',
	fieldType: 'string',
	fieldDescription: '',
	source: '',
	defaultValue: '',
	fieldReadOnly: false,
	fieldRequired: false,
	validate: false,
	disableType: false
};

export default injectIntl(EditField);
